import { useIdCounter } from '../../hooks';
import { getRequiredLabel } from '../../utils/internal';
import noop from '../../utils/noop';
import { FieldSet } from '../field-set';

import { HiddenInput, InputsWrapper, Label, LabelWrapper, Wrapper } from './toggle.styled';
import { IToggleInputProps, IToggleProps } from './types';

const ToggleInput: React.VFC<IToggleInputProps> = ({
  'aria-invalid': ariaInvalid,
  checked,
  'data-testid': testId,
  disabled,
  label,
  name,
  onChange = noop,
  required,
  value,
}) => {
  const inputId = useIdCounter(name);

  return (
    <LabelWrapper>
      <HiddenInput
        aria-invalid={ariaInvalid}
        checked={checked}
        data-testid={testId ? `${testId}-input-${value}` : undefined}
        disabled={disabled}
        id={inputId}
        name={name}
        onChange={() => onChange(value)}
        required={required}
        type="radio"
        value={value}
      />

      <Label
        checked={checked}
        data-testid={testId ? `${testId}-label-${value}` : undefined}
        disabled={disabled}
        htmlFor={inputId}
      >
        {label}
      </Label>
    </LabelWrapper>
  );
};

/**
 * A toggle allows the user to make a single choice from more than one option.
 */
const Toggle: React.VFC<IToggleProps> = ({
  'data-testid': testId,
  disabled,
  errorMessage,
  hintMessage,
  label,
  name,
  onChange,
  options,
  required,
  value,
  className,
}) => {
  return (
    <Wrapper data-testid={testId} className={className}>
      <FieldSet
        disabled={disabled}
        errorMessage={errorMessage}
        hintMessage={hintMessage}
        legend={label && getRequiredLabel(label, required)}
        data-testid={testId ? `${testId}-legend` : undefined}
      >
        <InputsWrapper>
          {options.map((option) => {
            return (
              <ToggleInput
                aria-invalid={Boolean(errorMessage)}
                checked={option.value === value}
                data-testid={testId}
                disabled={disabled || option.disabled}
                key={option.value}
                label={option.label}
                name={name}
                onChange={onChange}
                required={required}
                value={option.value}
              />
            );
          })}
        </InputsWrapper>
      </FieldSet>
    </Wrapper>
  );
};

export default Toggle;
